import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import MerchantRegister from './components/Auth/MerchantRegister';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import OAuthCallback from './components/Auth/OAuthCallback';
import CustomerDashboard from './components/Dashboard/CustomerDashboard';
import RiderDashboard from './components/Dashboard/RiderDashboard';
import MerchantDashboard from './components/Merchant/MerchantDashboard';
import MerchantProductInventory from './components/Merchant/MerchantProductInventory';
import MerchantDispatcherManagement from './components/Merchant/MerchantDispatcherManagement';
import MerchantInvoicing from './components/Merchant/MerchantInvoicing';
import AdminDashboard from './components/Dashboard/AdminDashboard';
import CreateOrder from './components/Orders/CreateOrder';
import OrdersList from './components/Orders/OrdersList';
import OrderTracking from './components/Orders/OrderTracking';
import PublicTracking from './components/Orders/PublicTracking';
import TrackOrders from './components/Orders/TrackOrders';
import BulkUpload from './components/Orders/BulkUpload';
import Wallet from './components/Wallet/Wallet';
import AvailableJobs from './components/Rider/AvailableJobs';
import MyJobs from './components/Rider/MyJobs';
import Earnings from './components/Rider/Earnings';
import JobHistory from './components/Rider/JobHistory';
import RiderProfile from './components/Rider/RiderProfile';
import RiderNotifications from './components/Rider/RiderNotifications';
import RiderSupport from './components/Rider/RiderSupport';
import RiderMessages from './components/Rider/RiderMessages';
import CompleteProfile from './components/Rider/CompleteProfile';
import EditRegistration from './components/Dispatcher/EditRegistration';
import PendingApprovals from './components/Admin/PendingApprovals';
import UserManagement from './components/Admin/UserManagement';
import CustomerManagement from './components/Admin/CustomerManagement';
import OrderManagement from './components/Admin/OrderManagement';
import DispatcherManagement from './components/Admin/DispatcherManagement';
import PlatformAnalytics from './components/Admin/PlatformAnalytics';
import SystemSettings from './components/Admin/SystemSettings';
import AdminManagement from './components/Admin/AdminManagement';
import AdminActivityLog from './components/Admin/AdminActivityLog';
import PricingManagement from './components/Admin/PricingManagement';
import WalletManagement from './components/Admin/WalletManagement';
import AdminWithdrawals from './components/Admin/AdminWithdrawals';
import AdminAnnouncements from './components/Admin/AdminAnnouncements';
import AdminSupport from './components/Admin/AdminSupport';
import AdminMerchantManagement from './components/Admin/AdminMerchantManagement';
import AdminCommunicationHub from './components/Admin/AdminCommunicationHub';
import CorporateApprovals from './components/Admin/CorporateApprovals';
import AdminDispatcherAssignment from './components/Admin/AdminDispatcherAssignment';
import TwoFactorSettings from './components/Admin/TwoFactorSettings';
import ProfileSettings from './components/Profile/ProfileSettings';
import PendingApprovalPage from './components/Dashboard/PendingApprovalPage';
import PromoReferrals from './components/Promo/PromoReferrals';
import Notifications from './components/Notifications/Notifications';
import Support from './components/Support/Support';
import NotFound from './components/Common/NotFound';
import LoadingScreen from './components/Common/LoadingScreen';
import PaymentCallback from './components/Customer/PaymentCallback';
import { InstallPrompt } from './components/PWA/InstallPrompt';
import VehicleCatalog from './components/LuxuryHire/Customer/VehicleCatalog';
import VehicleDetail from './components/LuxuryHire/Customer/VehicleDetail';
import BookingWizard from './components/LuxuryHire/Customer/BookingWizard';
import BookingConfirmation from './components/LuxuryHire/Customer/BookingConfirmation';
import LuxuryVehicleManagement from './components/LuxuryHire/Admin/VehicleManagement';

// Protected Route component
const ProtectedRoute: React.FC<{ 
  children: React.ReactNode; 
  allowedRoles?: string[];
  skipProfileCheck?: boolean;
  skipApprovalCheck?: boolean;
}> = ({ children, allowedRoles, skipProfileCheck = false, skipApprovalCheck = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen message="Preparing your dashboard..." />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if dispatcher needs to complete their profile (skip check for complete-profile page itself)
  if (!skipProfileCheck && user.role === 'rider' && user.profile_complete === false) {
    return <Navigate to="/complete-profile" replace />;
  }

  // Check if corporate customer needs approval (pending or rejected status)
  if (!skipApprovalCheck && user.role === 'customer' && user.customer_type === 'corporate') {
    if (user.approval_status === 'pending' || user.approval_status === 'rejected') {
      return <Navigate to="/pending-approval" replace />;
    }
  }

  // Check if rider/merchant needs approval
  if (!skipApprovalCheck && (user.role === 'rider' || user.role === 'merchant')) {
    if (user.approval_status === 'pending' || user.approval_status === 'rejected') {
      return <Navigate to="/pending-approval" replace />;
    }
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on user role
    switch (user.role) {
      case 'customer':
        return <Navigate to="/customer-dashboard" replace />;
      case 'rider':
        return <Navigate to="/rider-dashboard" replace />;
      case 'merchant':
        return <Navigate to="/merchant-dashboard" replace />;
      case 'admin':
        return <Navigate to="/admin-dashboard" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  return <>{children}</>;
};

// Public Route component (redirect to dashboard if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen message="Welcome to Volexx..." />;
  }

  if (user) {
    // Check if corporate customer needs approval
    if (user.role === 'customer' && user.customer_type === 'corporate') {
      if (user.approval_status === 'pending' || user.approval_status === 'rejected') {
        return <Navigate to="/pending-approval" replace />;
      }
    }

    // Check if rider/merchant needs approval
    if (user.role === 'rider' || user.role === 'merchant') {
      if (user.approval_status === 'pending' || user.approval_status === 'rejected') {
        return <Navigate to="/pending-approval" replace />;
      }
    }

    // Redirect to appropriate dashboard based on user role
    switch (user.role) {
      case 'customer':
        return <Navigate to="/customer-dashboard" replace />;
      case 'rider':
        return <Navigate to="/rider-dashboard" replace />;
      case 'merchant':
        return <Navigate to="/merchant-dashboard" replace />;
      case 'admin':
        return <Navigate to="/admin-dashboard" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  return <>{children}</>;
};

// Main App Routes component
const AppRoutes: React.FC = () => {
  return (
    <Layout>
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } 
        />
        <Route 
          path="/register/merchant" 
          element={
            <PublicRoute>
              <MerchantRegister />
            </PublicRoute>
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } 
        />
        <Route 
          path="/reset-password/:token" 
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          } 
        />
        <Route 
          path="/auth/callback" 
          element={<OAuthCallback />} 
        />
        
        {/* Public Order Tracking - No auth required */}
        <Route 
          path="/track/:orderUuid" 
          element={<PublicTracking />} 
        />
        
        <Route 
          path="/pending-approval" 
          element={
            <ProtectedRoute skipApprovalCheck={true}>
              <PendingApprovalPage />
            </ProtectedRoute>
          } 
        />

        {/* Customer Routes */}
        <Route 
          path="/customer-dashboard" 
          element={
            <ProtectedRoute allowedRoles={['customer']}>
              <CustomerDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/create-order" 
          element={
            <ProtectedRoute allowedRoles={['customer']}>
              <CreateOrder />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/orders/create" 
          element={
            <ProtectedRoute allowedRoles={['customer']}>
              <CreateOrder />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/orders" 
          element={
            <ProtectedRoute allowedRoles={['customer']}>
              <OrdersList />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/orders/:orderId" 
          element={
            <ProtectedRoute allowedRoles={['customer', 'admin', 'rider']}>
              <OrderTracking />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/wallet" 
          element={
            <ProtectedRoute allowedRoles={['customer']}>
              <Wallet />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/payment-callback" 
          element={
            <ProtectedRoute allowedRoles={['customer']}>
              <PaymentCallback />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/track-orders" 
          element={
            <ProtectedRoute allowedRoles={['customer']}>
              <TrackOrders />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/bulk-upload" 
          element={
            <ProtectedRoute allowedRoles={['customer']}>
              <BulkUpload />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/promo-referrals" 
          element={
            <ProtectedRoute allowedRoles={['customer']}>
              <PromoReferrals />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/notifications" 
          element={
            <ProtectedRoute allowedRoles={['customer']}>
              <Notifications />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/support" 
          element={
            <ProtectedRoute allowedRoles={['customer', 'rider', 'merchant']}>
              <Support />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profile-settings" 
          element={
            <ProtectedRoute allowedRoles={['customer', 'rider', 'merchant', 'admin']}>
              <ProfileSettings />
            </ProtectedRoute>
          } 
        />

        {/* Rider Routes */}
        <Route 
          path="/complete-profile" 
          element={
            <ProtectedRoute allowedRoles={['rider']} skipProfileCheck={true}>
              <CompleteProfile />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/rider-dashboard" 
          element={
            <ProtectedRoute allowedRoles={['rider']}>
              <RiderDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/available-jobs" 
          element={
            <ProtectedRoute allowedRoles={['rider']}>
              <AvailableJobs />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/my-jobs" 
          element={
            <ProtectedRoute allowedRoles={['rider']}>
              <MyJobs />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/earnings" 
          element={
            <ProtectedRoute allowedRoles={['rider']}>
              <Earnings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/job-history" 
          element={
            <ProtectedRoute allowedRoles={['rider']}>
              <JobHistory />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/rider-profile" 
          element={
            <ProtectedRoute allowedRoles={['rider']}>
              <RiderProfile />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/rider-notifications" 
          element={
            <ProtectedRoute allowedRoles={['rider']}>
              <RiderNotifications />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/rider-support" 
          element={
            <ProtectedRoute allowedRoles={['rider']}>
              <RiderSupport />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/rider-messages" 
          element={
            <ProtectedRoute allowedRoles={['rider']}>
              <RiderMessages />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/edit-registration" 
          element={
            <ProtectedRoute allowedRoles={['rider']}>
              <EditRegistration />
            </ProtectedRoute>
          } 
        />

        {/* Merchant Routes */}
        <Route 
          path="/merchant-dashboard" 
          element={
            <ProtectedRoute allowedRoles={['merchant']}>
              <MerchantDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/merchant/products" 
          element={
            <ProtectedRoute allowedRoles={['merchant']}>
              <MerchantProductInventory />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/merchant/inventory" 
          element={
            <ProtectedRoute allowedRoles={['merchant']}>
              <MerchantProductInventory />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/merchant/dispatchers" 
          element={
            <ProtectedRoute allowedRoles={['merchant']}>
              <MerchantDispatcherManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/manage-agents" 
          element={
            <ProtectedRoute allowedRoles={['merchant']}>
              <MerchantDispatcherManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/merchant/invoices" 
          element={
            <ProtectedRoute allowedRoles={['merchant']}>
              <MerchantInvoicing />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/merchant-orders" 
          element={
            <ProtectedRoute allowedRoles={['merchant']}>
              <MerchantDashboard />
            </ProtectedRoute>
          } 
        />

        {/* Admin Routes */}
        <Route 
          path="/admin" 
          element={<Navigate to="/admin-dashboard" replace />} 
        />
        <Route 
          path="/admin-dashboard" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/pending-approvals" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PendingApprovals />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/users" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <UserManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/customers" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CustomerManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/orders" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <OrderManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/dispatchers" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <DispatcherManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/analytics" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PlatformAnalytics />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/settings" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SystemSettings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/admins" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/wallets" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <WalletManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/withdrawals" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminWithdrawals />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/activity-log" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminActivityLog />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/pricing" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <PricingManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/announcements" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminAnnouncements />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/support" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminSupport />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/merchants" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminMerchantManagement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/communication" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminCommunicationHub />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/corporate-approvals" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <CorporateApprovals />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/dispatcher-assignment" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDispatcherAssignment />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/security" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <TwoFactorSettings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/luxury-vehicles" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <LuxuryVehicleManagement />
            </ProtectedRoute>
          } 
        />

        {/* Luxury Hire Routes - Public catalog, protected booking */}
        <Route 
          path="/luxury-hire" 
          element={<VehicleCatalog />} 
        />
        <Route 
          path="/luxury-hire/vehicle/:vehicleId" 
          element={<VehicleDetail />} 
        />
        <Route 
          path="/luxury-hire/book/:vehicleId" 
          element={
            <ProtectedRoute allowedRoles={['customer', 'admin']}>
              <BookingWizard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/luxury-hire/booking/:bookingId/confirmation" 
          element={
            <ProtectedRoute allowedRoles={['customer', 'admin']}>
              <BookingConfirmation />
            </ProtectedRoute>
          } 
        />

        {/* Default Routes */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <WebSocketProvider>
        <CurrencyProvider>
          <Router>
            <AppRoutes />
            <InstallPrompt />
          </Router>
        </CurrencyProvider>
      </WebSocketProvider>
    </AuthProvider>
  );
};

export default App;